import { setTargetEntityPropertiesFromSourceEntityArray } from '../siloRelated/setTargetEntityPropertiesFromSourceEntityArray'
import { Name } from './Name'
import { Person } from './Person'
import type { ICompanyContact, ICompanyOverview } from '~/types/company/models'
import type { TEntityId } from '~/types/models'
import {
  ContactTypeIds,
  getContactTypeById,
} from '~/services/selectLists/contactTypes'
import { ensureUniqueness } from '~/cynqueModules/ensureUniqueness'

const mn = '[cynqueModules / dataObjects / CompanyContact.ts]'

export function CompanyContact(o: ICompanyContact): ICompanyContact {
  const fn = `${mn} Contact |`
  if (!o) {
    throw new Error(`${fn} CompanyContact called with falsey customerContact`)
  }
  if (!o.person) {
    throw new Error(
      `${fn} CompanyContact called with falsey companyContact.person`
    )
  }
  if (!o.company) {
    throw new Error(
      `${fn} No company object in customerContact.` +
        ' This is almost certainly a programming error.'
    )
  }
  const obj = { ...o }
  obj.person = Person(o.person)
  obj.avatar = { descr: obj.person.name.initials ?? '', type: 'companyContact' }
  const nameDescr = Name(o.person.name).descr ?? 'No Name'
  const phoneNumberToUse =
    o.phone ?? o.cell ?? o.person.phone ?? o.person.cell ?? 'No Phone'
  obj.label = nameDescr
  obj.descr = nameDescr + '\n' + phoneNumberToUse
  obj.type = getContactTypeById(ContactTypeIds.COMPANY)
  obj.parentName = o.company.label
  obj.namePlusBiz = `${nameDescr} - ${obj.parentName} - ` + phoneNumberToUse
  obj.namePlusRole = `${nameDescr} - ${obj.role?.label ?? 'No role'}`

  console.debug(`${fn} returning`, obj)
  return obj
}

// Propagators

export function propagateCompanyContactData({
  overviewData,
}: {
  overviewData: ICompanyOverview
}) {
  //
  // embedded functions
  //
  function _propagateFromCompanyContactsToCompany({
    mn,
    overviewData,
  }: {
    mn: string
    overviewData: ICompanyOverview
  }) {
    const fn = `${mn} _propagateFromCompanyContactsToCompany |`
    const { company, companyContacts } = overviewData
    setTargetEntityPropertiesFromSourceEntityArray({
      propertyNames: ['isPrimaryContact', 'isBillingContact'],
      sourceEntityArray: companyContacts,
      createEntitySelectItemForTarget: false,
      // sourceEntitySelectItemIdPathString: 'contact.id',
      // sourceEntitySelectItemLabelPathString: 'contact.label',
      targetEntity: company,
    })
    console.debug(`${fn} overviewData after propagation:`, overviewData)
  }
  //
  // execution starts here...
  //
  const fn = `${mn} propagateCompanyContactData |`
  console.debug(`${fn} called`)
  _propagateFromCompanyContactsToCompany({
    mn,
    overviewData,
  })
}

/**
 * Combined hook run after a company contact is created/updated/deleted on a company
 * overview page. Enforces Primary/Billing flag mutual-exclusion (create/update only),
 * then re-derives the denormalized `company.primaryContact` / `company.billingContact`
 * the header binds to. Mirrors the bespoke handlers on the Operations page so the Sales
 * page (which uses the generic factory contact handlers) stays in sync.
 */
export function propagateCompanyContactChange({
  callingMn,
  overviewData,
  entityId,
  changedEntityData,
}: {
  callingMn: string
  overviewData: ICompanyOverview
  entityId: TEntityId
  changedEntityData?: Partial<ICompanyContact>
}) {
  const fn = `${callingMn} --> ${mn} propagateCompanyContactChange |`
  console.debug(`${fn} called`)
  // On create/update, if a Primary/Billing flag was part of this change, clear it on the
  // other rows. ensureUniqueness no-ops when the flag isn't present in changedEntityData,
  // so a phone-only edit leaves flags untouched. (Skipped on delete: changedEntityData is
  // undefined, and propagateCompanyContactData below clears a removed primary/billing.)
  if (changedEntityData) {
    for (const propName of ['isPrimaryContact', 'isBillingContact']) {
      ensureUniqueness({
        callingMn: fn,
        sourceEntityId: entityId,
        sourceEntityFlagPropertyName: propName,
        sourceEntityArray: overviewData.companyContacts,
        sourceEntityData: changedEntityData,
      })
    }
  }
  propagateCompanyContactData({ overviewData })
}
